import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Grid,
  Divider,
} from '@mui/material';
import {
  AttachMoney,
  TrendingUp,
  TrendingDown,
  Schedule,
  Warning,
  Assessment,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ExpensesService } from '../../utils/expenses-service';
import { ExpenseSummary } from '../../types';

interface ExpenseSummaryWidgetProps {
  className?: string;
  showHeader?: boolean;
  maxHeight?: number;
}

const ExpenseSummaryWidget: React.FC<ExpenseSummaryWidgetProps> = ({
  className = '',
  showHeader = true,
  maxHeight
}) => {
  const { user } = useAuth();
  const [summary, setSummary] = useState<ExpenseSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if ((user as any)?.userGroup === 'hosts') {
      loadExpenseSummary();
    }
  }, [user]);

  const loadExpenseSummary = async () => {
    try {
      setError(null);
      const summaryData = await ExpensesService.getExpenseSummary();
      setSummary(summaryData);
    } catch (err) {
      console.error('Error loading expense summary:', err);
      setError(err instanceof Error ? err.message : 'Failed to load expense summary');
    } finally {
      setLoading(false);
    }
  };

  if ((user as any)?.userGroup !== 'hosts') {
    return null;
  }

  if (loading) {
    return (
      <Card className={className} sx={{ height: maxHeight }}>
        <CardContent>
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className} sx={{ height: maxHeight }}>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button onClick={loadExpenseSummary} size="small">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!summary) {
    return (
      <Card className={className} sx={{ height: maxHeight }}>
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AttachMoney sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No expense data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start tracking your property expenses to see insights here
            </Typography>
            <Button
              variant="contained"
              component={Link}
              to="/expenses"
              startIcon={<AttachMoney />}
            >
              Add First Expense
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getTrendIcon = (trend: number) => {
    if (trend > 0) return <TrendingUp color="error" />;
    if (trend < 0) return <TrendingDown color="success" />;
    return null;
  };

  const getTrendColor = (trend: number) => {
    if (trend > 0) return 'error.main';
    if (trend < 0) return 'success.main';
    return 'text.secondary';
  };

  return (
    <Card className={className} sx={{ height: maxHeight, overflow: 'auto' }}>
      <CardContent>
        {showHeader && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              <AttachMoney sx={{ mr: 1, color: 'primary.main' }} />
              Expense Summary
            </Typography>
            <Button
              component={Link}
              to="/expenses"
              size="small"
              endIcon={<Assessment />}
            >
              View Details
            </Button>
          </Box>
        )}

        {/* Current Month Overview */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            This Month
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {formatCurrency(summary.currentMonth.total)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getTrendIcon(summary.currentMonth.trend)}
              <Typography 
                variant="body2" 
                sx={{ color: getTrendColor(summary.currentMonth.trend) }}
              >
                {summary.currentMonth.trend > 0 ? '+' : ''}{summary.currentMonth.trend.toFixed(1)}%
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {summary.currentMonth.count} expenses • vs last month
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Key Metrics */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {summary.recurringExpenses.active}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Active Recurring
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {summary.pendingReminders.count}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pending Reminders
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Alerts */}
        {summary.pendingReminders.count > 0 && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            action={
              <Button 
                component={Link} 
                to="/expenses" 
                size="small" 
                color="inherit"
              >
                Review
              </Button>
            }
          >
            {summary.pendingReminders.count} expense reminder{summary.pendingReminders.count > 1 ? 's' : ''} need{summary.pendingReminders.count === 1 ? 's' : ''} attention
          </Alert>
        )}

        {summary.overdueReminders.count > 0 && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              <Button 
                component={Link} 
                to="/expenses" 
                size="small" 
                color="inherit"
              >
                Review
              </Button>
            }
          >
            {summary.overdueReminders.count} overdue expense{summary.overdueReminders.count > 1 ? 's' : ''}
          </Alert>
        )}

        {/* Top Categories */}
        {summary.topCategories.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Top Categories This Month
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {summary.topCategories.slice(0, 3).map((category, index) => (
                <Box key={category.categoryId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={category.name} 
                      size="small" 
                      variant="outlined"
                      sx={{ minWidth: 80 }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(category.total)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Quick Actions */}
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                fullWidth
                size="small"
                component={Link}
                to="/expenses"
                startIcon={<AttachMoney />}
              >
                Add Expense
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                fullWidth
                size="small"
                component={Link}
                to="/expenses?tab=reports"
                startIcon={<Assessment />}
              >
                View Reports
              </Button>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ExpenseSummaryWidget;